import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  GraduationCap, CalendarDays, NotebookPen, Sparkles, Link2, Users, ArrowRight, Heart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const FEATURES = [
  { icon: CalendarDays, title: "Your day, at a glance", desc: "Daily & weekly agenda, events, and deadlines all on one calm homepage." },
  { icon: NotebookPen, title: "Plan without the chaos", desc: "Reusable daily and weekly lesson plans organized by subject and class." },
  { icon: Sparkles, title: "Smart drafting help", desc: "Draft lesson ideas, outlines, and parent emails in seconds. You stay in control." },
  { icon: Link2, title: "Your teacher toolbox", desc: "Every district tool, LMS link and form grouped and one click away." },
  { icon: Users, title: "Gentle student tracking", desc: "Lightweight academic and behavior notes — simple, never overwhelming." },
  { icon: Heart, title: "Made to feel like yours", desc: "Soft pastel themes, cozy layouts, and templates that fit how you teach." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background tcc-dots overflow-x-hidden">
      <header className="sticky top-0 z-30 bg-white/70 backdrop-blur-xl border-b border-white/50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-2xl bg-primary flex items-center justify-center shadow-sm">
              <GraduationCap className="text-primary-foreground" strokeWidth={1.6} />
            </div>
            <span className="font-heading font-extrabold text-lg text-slate-800">Teacher Command Center</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" data-testid="landing-login-btn" onClick={() => navigate(user ? "/dashboard" : "/login")} className="rounded-full text-slate-600">
              {user ? "Open app" : "Log in"}
            </Button>
            <Button data-testid="landing-signup-btn" onClick={() => navigate(user ? "/dashboard" : "/register")} className="rounded-full bg-primary hover:bg-primary/90">
              Get started
            </Button>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-12 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/40 text-slate-700 text-sm font-medium mb-6">
            <Sparkles size={15} className="text-primary" /> My whole teacher day in one place
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-800 leading-[1.05]">
            The cozy home base<br />for your teaching day.
          </h1>
          <p className="text-lg text-slate-600 mt-6 max-w-lg leading-relaxed">
            Stop bouncing between calendars, sticky notes, and a dozen browser tabs. Plan lessons, track your class, draft with AI, and see your whole day — beautifully, in one spot.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Button size="lg" data-testid="hero-cta-btn" onClick={() => navigate(user ? "/dashboard" : "/register")} className="rounded-full bg-primary hover:bg-primary/90 h-12 px-7 text-base">
              Get started free <ArrowRight size={18} className="ml-1" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/pricing")} className="rounded-full h-12 px-7 text-base border-slate-200">
              See Pro plans
            </Button>
          </div>
          <p className="text-sm text-slate-400 mt-4">Free plan available · No credit card to start · Demo: teacher@demo.com / teach123</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.15 }} className="relative">
          <div className="absolute -inset-6 bg-gradient-to-br from-primary/25 via-secondary/20 to-accent/30 blur-2xl rounded-full" />
          <div className="relative rounded-[2rem] bg-white p-3 shadow-2xl border border-slate-100">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/dc89b1b1-0d18-4a11-8fdc-7db21b7f990d/images/a8dc9cea5fa1f572096869c96fbd4177b0e6d3ac976b458fc10f8870ce1fe665.png"
              alt="Teacher Command Center dashboard shown on a laptop"
              className="w-full rounded-2xl object-cover"
            />
          </div>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-800 text-center mb-3">Everything a teacher runs on a day</h2>
        <p className="text-slate-500 text-center mb-12 max-w-xl mx-auto">Practical enough for every single day. Cute enough that you'll actually want to open it.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="tcc-card p-7 hover:-translate-y-1 transition-transform duration-200"
            >
              <div className="h-12 w-12 rounded-2xl bg-primary/12 flex items-center justify-center mb-4">
                <f.icon className="text-primary" strokeWidth={1.6} />
              </div>
              <h3 className="font-heading font-bold text-lg text-slate-800 mb-1.5">{f.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="tcc-card p-8">
            <h3 className="font-heading font-bold text-xl text-slate-800">Free</h3>
            <p className="text-slate-500 text-sm mt-1 mb-4">Everything you need to get organized.</p>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex gap-2"><Heart size={16} className="text-primary mt-0.5" /> Custom dashboard & widgets</li>
              <li className="flex gap-2"><Heart size={16} className="text-primary mt-0.5" /> Manual calendar (day/week)</li>
              <li className="flex gap-2"><Heart size={16} className="text-primary mt-0.5" /> Basic lesson planning</li>
              <li className="flex gap-2"><Heart size={16} className="text-primary mt-0.5" /> A handful of resource links</li>
            </ul>
          </div>
          <div className="tcc-card p-8 ring-2 ring-primary bg-gradient-to-br from-primary/10 via-white to-accent/15">
            <h3 className="font-heading font-bold text-xl text-slate-800 flex items-center gap-2">Pro <span className="text-xs px-2 py-0.5 rounded-full bg-primary text-primary-foreground">from $6/mo</span></h3>
            <p className="text-slate-500 text-sm mt-1 mb-4">The features that save you hours.</p>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex gap-2"><Sparkles size={16} className="text-primary mt-0.5" /> Two-way Google & Outlook sync</li>
              <li className="flex gap-2"><Sparkles size={16} className="text-primary mt-0.5" /> AI Smart Drafting</li>
              <li className="flex gap-2"><Sparkles size={16} className="text-primary mt-0.5" /> Student data tracking</li>
              <li className="flex gap-2"><Sparkles size={16} className="text-primary mt-0.5" /> All premium themes & unlimited links</li>
            </ul>
            <Button onClick={() => navigate("/pricing")} data-testid="landing-pricing-btn" className="rounded-full bg-primary hover:bg-primary/90 mt-6">See all plans</Button>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-20 pt-8">
        <div className="tcc-card p-10 sm:p-14 text-center bg-gradient-to-br from-primary/10 via-white to-accent/15">
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-800 mb-3">Ready to reclaim your prep time?</h2>
          <p className="text-slate-600 mb-7 max-w-md mx-auto">Set up your dashboard in under two minutes. No credit card, no clutter.</p>
          <Button size="lg" data-testid="footer-cta-btn" onClick={() => navigate(user ? "/dashboard" : "/register")} className="rounded-full bg-primary hover:bg-primary/90 h-12 px-8 text-base">
            Create my command center
          </Button>
        </div>
      </section>

      <footer className="border-t border-slate-100 py-8 text-center text-sm text-slate-400">
        Made with care for teachers. <Link to="/login" className="text-primary hover:underline">Log in</Link>
      </footer>
    </div>
  );
}
